import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { NavigationProps } from "../utils/navigationProps";
import { RootState } from "../store/rootReducer";
import { CardDownloader } from "../cards/downloader/cardDownloader";
import { markupFormatter } from "../common";
import { cardQuestionText } from "../utils/cardQuestionText";
import { usePageTitle } from "../hooks/usePageTitle";
import { NotFound404 } from "../components/NotFound404";
import { getTrainableTables } from "../markup/referenceViewer/trainableTables";
import { ModeSelect, TrainingMode } from "./modeSelect";
import { SequentialTraining } from "./sequentialTraining";
import { RandomTraining } from "./randomTraining";
import { Content, Message, Page, PageTitle } from "./trainUi";

type Props = NavigationProps & {
    cardId?: string;
};

export const TrainTables: FC<Props> = (props) => {
    const cardId = props.cardId;

    const selector = useCallback(
        (state: RootState) => (cardId !== undefined ? state.cards[cardId] : undefined),
        [cardId]
    );
    const card = useSelector(selector);

    const [notFound, setNotFound] = useState(false);
    useEffect(() => {
        if (cardId === undefined || cardId === "") {
            setNotFound(true);
            return;
        }
        setNotFound(false);
        if (card !== undefined)
            return;
        const timer = setTimeout(() => {
            if (card === undefined)
                setNotFound(true);
        }, 800);
        return () => clearTimeout(timer);
    }, [cardId, card]);

    const tables = useMemo(
        () => (card !== undefined ? getTrainableTables(markupFormatter.parse(card.markup).reference) : []),
        [card]
    );

    const question = card !== undefined ? cardQuestionText(card.markup) : "";
    usePageTitle(`Train · ${question || "Card"} · Memkeep`);

    const [mode, setMode] = useState<TrainingMode | null>(null);

    if (notFound)
        return <NotFound404 type="card" id={cardId} />;

    return (
        <Page>
            <CardDownloader cardId={cardId} />
            <Content>
                <PageTitle>{question}</PageTitle>
                {card === undefined ? (
                    <Message>Loading…</Message>
                ) : tables.length === 0 ? (
                    <Message>This card has no tables to train.</Message>
                ) : mode === null ? (
                    <ModeSelect onSelect={setMode} />
                ) : mode === "sequential" ? (
                    <SequentialTraining tables={tables} />
                ) : (
                    <RandomTraining tables={tables} />
                )}
            </Content>
        </Page>
    );
};
